import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { addDocument, findDocumentsByField } from '../services/firestoreService';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  LinearProgress,
  Input,
} from '@mui/material';

const SalesImporter = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(0);

  const handleFileChange = (e) => {
    setError('');
    setSuccess('');
    setProgress(0);
    setFile(e.target.files?.[0] || null);
  };

  const normalizeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;

    const raw = String(value).trim();
    if (!raw) return 0;

    // Eliminar símbolos de moneda y espacios
    const cleaned = raw.replace(/[^0-9,.-]/g, '');

    // Detectar separador decimal: último "," o "."
    const commaIndex = cleaned.lastIndexOf(',');
    const dotIndex = cleaned.lastIndexOf('.');

    let normalized = cleaned;
    if (commaIndex > dotIndex) {
      // Formato europeo: 1.234,56 -> 1234.56
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // Formato US: 1,234.56 -> 1234.56
      normalized = cleaned.replace(/,/g, '');
    }

    const num = parseFloat(normalized);
    return Number.isFinite(num) ? num : 0;
  };

  const getValueByCandidates = (row, candidates) => {
    const keys = Object.keys(row || {});
    for (const candidate of candidates) {
      const foundKey = keys.find((k) => k.trim().toLowerCase().startsWith(candidate.toLowerCase()));
      if (foundKey) return row[foundKey];
    }
    return '';
  };

  const importRows = async (rows) => {
    if (!rows || rows.length === 0) {
      setError('El archivo no contiene filas de datos.');
      setLoading(false);
      return;
    }

    let imported = 0;
    let duplicates = 0;

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      try {
        const metaName = getValueByCandidates(row, ['meta']);
        const period = getValueByCandidates(row, ['periodo', 'period']);
        const budgetId = getValueByCandidates(row, ['id_presupuesto', 'id presupuesto', 'id']);
        const ventaRaw = getValueByCandidates(row, ['venta $', 'venta$', 'venta']);

        if (!metaName && !budgetId && !ventaRaw) {
          // Fila completamente vacía: la saltamos
          setProgress(Math.round(((i + 1) / rows.length) * 100));
          continue;
        }

        if (!budgetId) {
          // Sin ID de presupuesto no podemos relacionar la venta: la saltamos
          // pero seguimos con el resto
          setProgress(Math.round(((i + 1) / rows.length) * 100));
          continue;
        }

        const amount = normalizeNumber(ventaRaw);
        const compositeId = `${String(metaName || '').trim()}::${String(period || '').trim()}::${amount}`;

        // Evitar duplicados por combinación Meta + Periodo + Amount
        const existing = await findDocumentsByField('Sales', 'compositeId', compositeId);
        if (existing && existing.length > 0) {
          duplicates += 1;
          setProgress(Math.round(((i + 1) / rows.length) * 100));
          continue;
        }

        const doc = {
          meta: String(metaName || '').trim(),
          period: String(period || '').trim(),
          budgetId: String(budgetId || '').trim(),
          amount,
          rawAmount: ventaRaw,
          compositeId,
          createdAt: new Date(),
        };

        await addDocument('Sales', doc);
        imported += 1;
        setProgress(Math.round(((i + 1) / rows.length) * 100));
      } catch (err) {
        console.error('Error procesando fila de ventas:', row, err);
        setError(`Error al procesar la fila ${i + 1}. La importación se ha detenido.`);
        setLoading(false);
        return;
      }
    }

    const duplicateInfo = duplicates
      ? ` (${duplicates} filas omitidas por duplicado Meta + Periodo + Amount)`
      : '';
    setSuccess(`${imported} de ${rows.length} filas importadas con éxito.${duplicateInfo}`);
    setLoading(false);
  };

  const processImport = async () => {
    if (!file) {
      setError('Por favor, selecciona un archivo Excel o CSV.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);

    const fileName = file.name || '';
    const ext = fileName.split('.').pop()?.toLowerCase() || '';

    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const rows = results.data || [];
          await importRows(rows);
        },
        error: (err) => {
          console.error('Error al leer el archivo CSV de ventas:', err);
          setError('Error al leer el archivo CSV: ' + err.message);
          setLoading(false);
        },
      });
      return;
    }

    if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          await importRows(rows);
        } catch (err) {
          console.error('Error al procesar el archivo Excel de ventas:', err);
          setError('Error al procesar el archivo Excel. Revisa el formato.');
          setLoading(false);
        }
      };
      reader.onerror = (err) => {
        console.error('Error de lectura del archivo Excel:', err);
        setError('No se pudo leer el archivo Excel.');
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    setError('Formato de archivo no soportado. Usa .xlsx, .xls o .csv.');
    setLoading(false);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Importar Ventas Reales desde Excel
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        El archivo debe tener cuatro columnas: <strong>Meta</strong>, <strong>Periodo</strong> (formato YYYY-MM, ej: 2024-12), <strong>ID_PRESUPUESTO</strong> y <strong>Venta $</strong>.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Importante:</strong> El campo <strong>Periodo</strong> debe estar en formato <strong>YYYY-MM</strong> (ejemplo: 2024-12 para Diciembre 2024)
      </Alert>
      <Box>
        <Input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          onClick={processImport}
          disabled={loading || !file}
        >
          {loading ? 'Importando...' : 'Importar Ventas'}
        </Button>
      </Box>
      {loading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="body2" color="text.secondary">{`${progress}%`}</Typography>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
    </Paper>
  );
};

export default SalesImporter;
